import { sendGaEvent } from '@/lib/analytics/ga'
import { Sentry, isObservabilityEnabled } from '@/lib/observability/sentry'

export type ClubUxEventName =
  // THE CLUBS DIRECTORY — the steps on /clubs, before any club page is opened. These five
  // arrived from a SECOND dictionary (`features/analytics/clubs.ts`, category `ux.clubs`)
  // which wrote Sentry breadcrumbs and reached GA4 never. They live here now so that the
  // whole club journey has one vocabulary and one sender; that file explains each name.
  | 'clubs_view'
  | 'clubs_search'
  | 'clubs_filter'
  | 'clubs_near_me_enabled'
  // The step immediately before `club_page_view`, and the head of the directory funnel:
  // a club was chosen out of a list. Until now it was not in the data at all.
  | 'club_card_open'
  | 'club_page_view'
  | 'club_cta_click'
  | 'club_join_success'
  | 'club_join_fail'
  // The other end of the membership: joining is the conversion, leaving is the churn, and
  // one is not readable without the other.
  | 'club_leave_success'
  | 'club_reserve_entry'
  // How the booking the entry above opened actually ended — see `clubFunnel.ts`.
  | 'club_booking_success'
  | 'club_booking_fail'
  | 'club_activity_open_tournament'
  | 'club_availability_view'
  | 'club_availability_slot_click'
  | 'club_amenities_view'
  | 'club_gallery_view'
  | 'club_gallery_open'

type TrackPayload = Record<string, string | number | boolean | null | undefined>

function normalizePayload(payload: TrackPayload | undefined): Record<string, string | number | boolean> | undefined {
  if (!payload) return undefined
  const next: Record<string, string | number | boolean> = {}
  Object.entries(payload).forEach(([key, value]) => {
    if (value == null) return
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      next[key] = value
    }
  })
  return Object.keys(next).length ? next : undefined
}

/**
 * Record one club-UX step in BOTH places it has to land: a GA4 event, which is where the
 * owner reads the funnel, and a Sentry breadcrumb, which is the trail attached to a crash
 * report — and only ever to a crash report.
 *
 * THE ORDER OF THESE FOUR LINES IS THE WHOLE POINT. The Sentry guard used to be the FIRST
 * line, so every name in the union above returned before doing anything at all whenever
 * `VITE_SENTRY_DSN` was unset — which was every environment we ran until the deploy
 * workflow began supplying it (`c654285f`, 2026-08-26), and is still every local one.
 * Those events were not merely hard to find in GA4; they were never sent anywhere.
 *
 * AND A KEY DOES NOT FIX IT, which is why the order still matters now that prod has one: a
 * breadcrumb is carried by an error report, so it exists only inside sessions that crashed
 * and only for as long as that report. A funnel cannot be built out of those, however many
 * DSNs are configured. GA4 goes first and unconditionally; the breadcrumb is the part that
 * is skipped when Sentry is off.
 *
 * GA4 delivery inherits the consent gate from `sendGaEvent` — nothing is sent, queued or
 * stored until the visitor accepts analytics. Do not re-check consent here: one gate, in
 * one place, is why there is nothing to keep in step.
 *
 * AND THIS IS THE ONLY SENDER OF A CLUB STEP. There used to be a second — `recordEvent` in
 * `features/analytics/clubs.ts`, seven names of its own under the Sentry category
 * `ux.clubs` — and it was mute for exactly as long as this one was, because both had been
 * built on the same mistake. Its names were folded into the union above and the function
 * was DELETED, not repaired: a second route to a sink is a second route to lose one, and
 * the way this defect stays fixed is that there is nowhere else for a club step to go.
 *
 * `orgId` IS ALLOWED IN THIS PAYLOAD, and the ban on it a few lines below is not an
 * oversight. The owner's question about these steps is comparative — "of ten who looked at
 * club A, three booked; at club B, none" — and it cannot be asked without naming the club.
 * `trackConversion` feeds Google Ads, where a club identifier is of use to nobody, so it
 * keeps sending none. What NEITHER may carry is a person: no name, no email, no user id,
 * no booking id.
 */
export function trackEvent(name: ClubUxEventName, payload?: TrackPayload): void {
  const params = normalizePayload(payload)
  sendGaEvent(name, params)
  if (!isObservabilityEnabled()) return
  Sentry.addBreadcrumb({ category: 'ux.club', level: 'info', message: name, data: params })
}

/** The club-signup funnel, in order: CTA click → registration form reached →
 *  account confirmed → club created. `club_created` is the business conversion
 *  paid campaigns optimise against, which is why these four travel on from GA4
 *  to Google Ads once the accounts are linked — and why, unlike the club UX
 *  names above, they carry NO club id: an advertising account has no use for
 *  one. That difference is deliberate, not drift. */
export type ConversionEventName =
  | 'cta_start_club_click'
  | 'signup_started'
  | 'signup_completed'
  | 'club_created'

/** Record one funnel step: a GA4 event (campaign measurement) plus a Sentry
 *  breadcrumb (debugging). GA4 delivery inherits the consent gate from
 *  sendGaEvent — it no-ops until the visitor accepts analytics. Payloads must be
 *  coarse and aggregate-safe: no names, emails, user ids, club ids or slugs. */
export function trackConversion(name: ConversionEventName, payload?: TrackPayload): void {
  const params = normalizePayload(payload)
  sendGaEvent(name, params)
  if (!isObservabilityEnabled()) return
  Sentry.addBreadcrumb({ category: 'funnel', level: 'info', message: name, data: params })
}
